import React, { useEffect, useState } from "react";
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GetData from "../../services/GetData.js";

const PACK_LABELS = [
    { key: "retail", label: "Retail" },
    { key: "single", label: "Single" },
    { key: "singleBiab", label: "Single BIAB" },
    { key: "double", label: "Double" },
    { key: "doubleBiab", label: "Double BIAB" },
    { key: "pack3x", label: "3x" },
    { key: "pack3xBiab", label: "3x BIAB" },
    { key: "pack4x", label: "4x" },
    { key: "pack4xBiab", label: "4x BIAB" },
];

function DetailRow({ label, value }) {
    if (value == null || value === "") return null;
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                gap: 1,
                py: 0.5,
                fontSize: 13,
            }}
        >
            <Typography sx={{ color: "#64748B", fontWeight: 600 }}>{label}</Typography>
            <Typography sx={{ color: "#0F172A" }}>{String(value)}</Typography>
        </Box>
    );
}

function packHasData(pack) {
    if (!pack) return false;
    return (
        pack.weight != null ||
        pack.length != null ||
        pack.width != null ||
        pack.height != null
    );
}

/**
 * Shows Product (SKU) details and optional SkuMetric variant packs.
 * Pass either productId, or a preloaded `product` / `variant` from the grid row.
 */
export default function SkuDetailDialog({
    open,
    onClose,
    productId,
    product: productProp,
    variant: variantProp,
}) {
    const [loading, setLoading] = useState(false);
    const [product, setProduct] = useState(productProp || null);
    const [variants, setVariants] = useState(variantProp ? [variantProp] : []);

    useEffect(() => {
        if (!open) return;

        setProduct(productProp || null);
        setVariants(variantProp ? [variantProp] : []);

        const load = async () => {
            const id = productId || productProp?._id;
            if (!id) return;
            setLoading(true);
            try {
                if (!productProp || !productProp.listingField) {
                    const { getData } = await GetData(`broylesllc/products/${id}`);
                    if (getData) setProduct(getData);
                }
                const { getData: metrics } = await GetData(
                    `broylesllc/sku-metrics?productId=${id}`
                );
                if (metrics?.items?.length) {
                    setVariants(metrics.items);
                } else if (variantProp) {
                    setVariants([variantProp]);
                }
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [open, productId, productProp, variantProp]);

    const title =
        product?.listingField ||
        product?.internalSkuId ||
        variantProp?.label ||
        "SKU details";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pr: 1,
                }}
            >
                <Typography component="span" sx={{ fontWeight: 700, fontSize: 18 }}>
                    {title}
                </Typography>
                <IconButton onClick={onClose} size="small" aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {!loading && product && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>
                            Product
                        </Typography>
                        <DetailRow label="Listing Field" value={product.listingField} />
                        <DetailRow label="Internal SKU" value={product.internalSkuId} />
                        <DetailRow label="Item Name" value={product.itemName} />
                        <DetailRow label="AZ Category" value={product.azCategory} />
                        <DetailRow label="Condition" value={product.conditionGrade} />
                        <DetailRow label="ASIN" value={product.asin} />
                        <DetailRow label="UPC" value={product.upc} />
                        <DetailRow label="MSRP" value={product.msrpUsd} />
                        <DetailRow
                            label="Qty eBay"
                            value={product.availableQuantityEbay}
                        />
                        <DetailRow
                            label="Qty Walmart"
                            value={product.availableQuantityWalmart}
                        />
                    </Box>
                )}
                {!loading && !product && (
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        No product linked to this catalog item.
                    </Typography>
                )}

                {!loading &&
                    variants.map((v) => {
                        const packs = PACK_LABELS.filter(({ key }) =>
                            packHasData(v.packs?.[key])
                        );
                        const customPacks = (Array.isArray(v.customPacks) ? v.customPacks : []).filter(
                            (p) => packHasData(p) && String(p?.name || "").trim()
                        );
                        const hasPackRows = packs.length > 0 || customPacks.length > 0;
                        return (
                            <Box key={v._id || v.label} sx={{ mt: 1 }}>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>
                                    Variant / shipping metrics
                                </Typography>
                                <DetailRow label="Label" value={v.label} />
                                <DetailRow label="Internal SKU" value={v.internalSkuId} />
                                <DetailRow label="UPC" value={v.upc} />
                                <DetailRow label="Year" value={v.yearApprox} />
                                <DetailRow label="Box Value" value={v.boxValue} />
                                <DetailRow label="MPN" value={v.mpn} />

                                {hasPackRows && (
                                    <Table size="small" sx={{ mt: 1.5 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Pack</TableCell>
                                                <TableCell>Weight</TableCell>
                                                <TableCell>L</TableCell>
                                                <TableCell>W</TableCell>
                                                <TableCell>H</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {packs.map(({ key, label }) => {
                                                const p = v.packs[key] || {};
                                                return (
                                                    <TableRow key={key}>
                                                        <TableCell>{label}</TableCell>
                                                        <TableCell>{p.weight ?? "—"}</TableCell>
                                                        <TableCell>{p.length ?? "—"}</TableCell>
                                                        <TableCell>{p.width ?? "—"}</TableCell>
                                                        <TableCell>{p.height ?? "—"}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            {customPacks.map((p) => (
                                                <TableRow key={`custom-${p.name}`}>
                                                    <TableCell>{p.name}</TableCell>
                                                    <TableCell>{p.weight ?? "—"}</TableCell>
                                                    <TableCell>{p.length ?? "—"}</TableCell>
                                                    <TableCell>{p.width ?? "—"}</TableCell>
                                                    <TableCell>{p.height ?? "—"}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Box>
                        );
                    })}
            </DialogContent>
        </Dialog>
    );
}
